/**
 * useCashierActions Hook - Coordinator Pattern Implementation
 *
 * This hook coordinates cashier-related actions:
 * - Validate cashier code for redemptions
 *
 * Pattern: COORDINATOR
 * - Orchestrates cashier validation mutations
 * - Returns Promises (no data storage)
 * - Loading states per action
 * - No computed values or aggregation
 *
 * IMPORTANT: This hook manages cashier actions only.
 * For redemption actions, use the useRedemptionActions coordinator hook.
 */

import { useCheckCashierCode, useCheckCouponCode } from '@/generated/hooks';
import type {
	CheckCashierCodeBodyType,
	CheckCashierCodeResponseType,
	CheckCouponCodeBodyType,
	CheckCouponCodeResponseType,
} from '@zlinkx/shared-v2';

// ============================================================================
// TYPES
// ============================================================================

/**
 * Options for useCashierActions hook
 */
interface UseCashierActionsOptions {
	/**
	 * Callback on successful cashier validation
	 */
	onValidateSuccess?: (data: CheckCashierCodeResponseType['data']) => void;
	/**
	 * Callback on cashier validation error
	 */
	onValidateError?: (error: Error) => void;
	/**
	 * Callback on successful coupon validation (Story 7.3)
	 */
	onCouponValidateSuccess?: (data: CheckCouponCodeResponseType['data']) => void;
	/**
	 * Callback on coupon validation error (Story 7.3)
	 */
	onCouponValidateError?: (error: Error) => void;
}

/**
 * Cashier actions available
 */
interface CashierActions {
	/**
	 * Validate cashier code
	 */
	validateCashierCode: (merchantId: number, code: string) => Promise<CheckCashierCodeResponseType['data']>;
	/**
	 * Validate coupon code (QR or manual entry) - Story 7.3
	 */
	validateCouponCode: (
		merchantId: number,
		code: string,
	) => Promise<CheckCouponCodeResponseType['data']>;
}

/**
 * Loading states per action
 */
interface CashierActionsLoading {
	validateCashierCode: boolean;
	validateCouponCode: boolean;
}

/**
 * Return type for useCashierActions hook
 */
interface UseCashierActionsReturn extends CashierActions {
	/**
	 * Loading states per action
	 */
	isLoading: CashierActionsLoading;
	/**
	 * Check if cashier validation is pending
	 */
	isValidating: boolean;
	/**
	 * Check if coupon validation is pending (Story 7.3)
	 */
	isValidatingCoupon: boolean;
}

// ============================================================================
// HOOK IMPLEMENTATION
// ============================================================================

/**
 * Coordinates cashier-related actions
 *
 * @example
 * ```tsx
 * function CashierCodeEntry({ merchantId }: Props) {
 *   const cashierActions = useCashierActions({
 *     onValidateSuccess: (result) => {
 *       setCashierInfo({
 *         cashierId: result.cashier_id,
 *         cashierName: result.cashier_name,
 *       });
 *     },
 *     onValidateError: (error) => {
 *       toast.error('Invalid cashier code');
 *     },
 *   });
 *
 *   const handleValidate = async (code: string) => {
 *     try {
 *       const result = await cashierActions.validateCashierCode(merchantId, code);
 *       return result;
 *     } catch (error) {
 *       // Error handled by onValidateError
 *       throw error;
 *     }
 *   };
 *
 *   return (
 *     <CashierCodeInput
 *       onValidate={handleValidate}
 *       isValidating={cashierActions.isValidating}
 *     />
 *   );
 * }
 * ```
 */
export function useCashierActions(
	options: UseCashierActionsOptions = {},
): UseCashierActionsReturn {
	const { onValidateSuccess, onValidateError, onCouponValidateSuccess, onCouponValidateError } = options;

	// ========================================
	// Generated Hooks (API layer)
	// ========================================

	const checkCashierMutation = useCheckCashierCode({
		onSuccess: data => {
			onValidateSuccess?.(data);
		},
		onError: error => {
			onValidateError?.(error);
		},
	});

	// Story 7.3: Coupon validation mutation
	const checkCouponMutation = useCheckCouponCode({
		onSuccess: data => {
			onCouponValidateSuccess?.(data);
		},
		onError: error => {
			onCouponValidateError?.(error);
		},
	});

	// ========================================
	// Action Functions
	// ========================================

	/**
	 * Validate cashier code
	 */
	const validateCashierCode = async (
		merchantId: number,
		code: string,
	): Promise<CheckCashierCodeResponseType['data']> => {
		const data: CheckCashierCodeBodyType = { code };
		const response = await checkCashierMutation.mutateAsync({
			merchantId,
			data,
		});
		return response;
	};

	/**
	 * Validate coupon code (QR or manual entry) - Story 7.3
	 * Returns the unwrapped data directly for easy consumption
	 */
	const validateCouponCode = async (
		merchantId: number,
		code: string,
	): Promise<CheckCouponCodeResponseType['data']> => {
		const data: CheckCouponCodeBodyType = { code };
		const response = await checkCouponMutation.mutateAsync({
			merchantId,
			data,
		});
		return response;
	};

	// ========================================
	// Return Coordinator Pattern
	// ========================================

	return {
		// Actions
		validateCashierCode,
		validateCouponCode,

		// Loading states
		isLoading: {
			validateCashierCode: checkCashierMutation.isPending,
			validateCouponCode: checkCouponMutation.isPending,
		},
		isValidating: checkCashierMutation.isPending,
		isValidatingCoupon: checkCouponMutation.isPending,
	};
}

// ============================================================================
// EXPORTS
// ============================================================================

export default useCashierActions;
export type {
	UseCashierActionsOptions,
	CashierActions,
	CashierActionsLoading,
	UseCashierActionsReturn,
	CheckCashierCodeBodyType,
	CheckCashierCodeResponseType,
	CheckCouponCodeBodyType,
	CheckCouponCodeResponseType,
};
